import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@hooks/notifications/useNotifications';
import { useNotificationActions } from '@hooks/notifications/useNotificationActions';
import type { NotificationDto } from '@api/notifications';
import NotificationItem from './NotificationItem';
import NotificationEmptyState from './NotificationEmptyState';
import {
  ClearAllButton,
  HeaderActions,
  HeaderLabel,
  HeaderRow,
  LoadMoreSentinel,
  ScrollArea,
  StatusRow,
  Wrapper,
} from './NotificationList.styled';

interface NotificationListProps {
  /** Hide the inline header (label + clear-all). */
  hideHeader?: boolean;
  /** Override the inline header label. */
  headerLabel?: string;
  /** Optional callback fired when the user clicks a notification with an actionUrl. */
  onNotificationOpen?: () => void;
  dataTestId?: string;
}

/**
 * Self-contained, paginated notification list with:
 *   - infinite scroll (IntersectionObserver on a sentinel)
 *   - per-item delete (X button on hover/focus)
 *   - "Clear all" header action
 *   - click-to-open with mark-read side effect
 *
 * Designed to be embedded inside a popover (e.g. ProfileMenu) — the parent
 * controls panel chrome, this component owns the data + interactions.
 */
const NotificationList: React.FC<NotificationListProps> = ({
  hideHeader = false,
  headerLabel,
  onNotificationOpen,
  dataTestId = 'notification-list',
}) => {
  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useNotifications(false);
  const { markRead, clearOne, clearAll } = useNotificationActions();

  const items = useMemo<readonly NotificationDto[]>(
    () => (data?.pages ?? []).flatMap((page) => page.items),
    [data],
  );

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    if (!hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          // Fire-and-forget: react-query handles errors via the query state.
          fetchNextPage().catch(() => undefined);
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleItemClick = useCallback(
    (notification: NotificationDto) => {
      if (!notification.readAt) markRead.mutate(notification.id);
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
        onNotificationOpen?.();
      }
    },
    [markRead, navigate, onNotificationOpen],
  );

  const handleDelete = useCallback(
    (id: string) => {
      clearOne.mutate(id);
    },
    [clearOne],
  );

  const isEmpty = !isLoading && items.length === 0;

  return (
    <Wrapper data-testid={dataTestId}>
      {!hideHeader && (
        <HeaderRow>
          <HeaderLabel>{headerLabel ?? 'Notifications'}</HeaderLabel>
          <HeaderActions>
            <ClearAllButton
              type="button"
              onClick={() => clearAll.mutate()}
              disabled={isEmpty || clearAll.isPending}
              data-testid={`${dataTestId}__clear-all`}
            >
              Clear all
            </ClearAllButton>
          </HeaderActions>
        </HeaderRow>
      )}

      <ScrollArea data-testid={`${dataTestId}__scroll`}>
        {isLoading && <StatusRow>Loading notifications…</StatusRow>}
        {isEmpty && <NotificationEmptyState />}
        {items.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClick={() => handleItemClick(notification)}
            onDelete={() => handleDelete(notification.id)}
          />
        ))}
        {hasNextPage && (
          <LoadMoreSentinel
            ref={sentinelRef}
            data-testid={`${dataTestId}__sentinel`}
          />
        )}
        {isFetchingNextPage && <StatusRow>Loading more…</StatusRow>}
      </ScrollArea>
    </Wrapper>
  );
};

export default NotificationList;
